/*
 * Behaviour for the modern header (snippets/header-modern.liquid).
 *
 * - <header-drawer-modern>: mobile drawer whose submenus expand in place
 *   (native <details> accordions) instead of sliding to a second panel.
 * - <header-search-toggle>: shows/hides the search bar row on mobile.
 * - <header-modern-nav>: opens desktop dropdowns/mega panels on hover for
 *   mouse users; click/keyboard keep working through details-disclosure.js.
 *
 * Sticky behaviour, focus trapping (trapFocus/removeTrapFocus from global.js)
 * and the search suggestions are shared with the classic header.
 */
(() => {
  const DESKTOP_QUERY = '(min-width: 990px)';

  const focusTrap = (container, target) => {
    if (typeof trapFocus === 'function') {
      trapFocus(container, target || container);
    } else if (target) {
      target.focus();
    }
  };

  const releaseFocusTrap = (target) => {
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(target);
    } else if (target) {
      target.focus();
    }
  };

  class HeaderDrawerModern extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('.hm-menu-toggle');
      this.drawer = this.querySelector('.hm-drawer');
      this.overlay = this.querySelector('.hm-drawer-overlay');
      if (!this.toggleButton || !this.drawer) return;

      this.toggleButton.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && this.isOpen) this.close();
      });

      this.drawer.querySelectorAll('details.hm-acc').forEach((details) => {
        details.addEventListener('toggle', () => this.onAccordionToggle(details));
      });

      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.onDesktopChange = (event) => {
        if (event.matches && this.isOpen) this.close(false);
      };
      this.desktopQuery.addEventListener('change', this.onDesktopChange);
    }

    disconnectedCallback() {
      if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onDesktopChange);
      if (this.isOpen) this.close(false);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    get sectionElement() {
      return this.closest('.section-header') || this.closest('.shopify-section') || document.querySelector('.section-header');
    }

    open() {
      const section = this.sectionElement;
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      if (section) {
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${Math.round(section.getBoundingClientRect().bottom)}px`
        );
        section.classList.add('menu-open');
      }

      this.classList.add('is-open');
      this.toggleButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden-tablet');
      // Keep focus on the (visible) toggle; the drawer is still transitioning in
      // and cannot receive focus yet. Tab moves into the drawer from here.
      focusTrap(this, this.toggleButton);
    }

    close(returnFocus = true) {
      this.classList.remove('is-open');
      this.toggleButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden-tablet');
      const section = this.sectionElement;
      if (section) section.classList.remove('menu-open');
      releaseFocusTrap(returnFocus ? this.toggleButton : null);
    }

    // Keep a single submenu open per level so the list never grows past the screen.
    onAccordionToggle(details) {
      if (!details.open) return;
      const list = details.closest('ul');
      if (!list) return;
      list.querySelectorAll(':scope > li > details.hm-acc[open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    }
  }

  if (!customElements.get('header-drawer-modern')) {
    customElements.define('header-drawer-modern', HeaderDrawerModern);
  }

  class HeaderSearchToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.header = this.closest('.header--modern');
      this.searchRow = this.header ? this.header.querySelector('.hm-search') : null;
      if (!this.button || !this.header || !this.searchRow) return;

      this.button.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      this.searchRow.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && this.isOpen) this.close();
      });

      this.onDocumentClick = (event) => {
        if (this.isOpen && !this.header.contains(event.target)) this.close(false);
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    get isOpen() {
      return this.header.classList.contains('hm-search-open');
    }

    open() {
      this.header.classList.add('hm-search-open');
      this.button.setAttribute('aria-expanded', 'true');
      // Let the row's display:block apply before moving focus into it.
      setTimeout(() => {
        const input = this.searchRow.querySelector('input[type="search"]');
        if (input) input.focus();
      }, 20);
    }

    close(returnFocus = true) {
      this.header.classList.remove('hm-search-open');
      this.button.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.button.focus();
    }
  }

  if (!customElements.get('header-search-toggle')) {
    customElements.define('header-search-toggle', HeaderSearchToggle);
  }

  class HeaderModernNav extends HTMLElement {
    connectedCallback() {
      this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!this.hoverCapable) return;

      this.querySelectorAll('header-menu').forEach((menu) => {
        const details = menu.querySelector('details');
        const summary = menu.querySelector('summary');
        if (!details || !summary) return;
        let timer;

        menu.addEventListener('mouseenter', () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            this.closeOthers(details);
            details.open = true;
          }, 60);
        });

        menu.addEventListener('mouseleave', () => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            details.open = false;
          }, 180);
        });

        // With hover-to-open, a mouse click on an already open item should not
        // snap the panel shut: follow the item's own link when it has one,
        // otherwise leave the panel as it is. Keyboard activation (detail 0)
        // keeps the native toggle.
        summary.addEventListener('click', (event) => {
          if (event.detail === 0 || !details.open) return;
          event.preventDefault();
          const url = summary.dataset.url;
          if (url) window.location.assign(url);
        });
      });
    }

    closeOthers(current) {
      this.querySelectorAll('details[open]').forEach((details) => {
        if (details !== current) details.open = false;
      });
    }
  }

  if (!customElements.get('header-modern-nav')) {
    customElements.define('header-modern-nav', HeaderModernNav);
  }
})();
